import React, { ErrorInfo, Suspense, useCallback, useEffect, useState } from 'react'
import { useGrades, useRegion, useAdminRegion, useSemesters } from '@/store/dict'
import { checkoutLogin, isInternalUser, getUserLoginType, getTokenTenantId, USER_LOGIN_TYPE } from '@yuanfudao/metis-unified-login'
import Navbar from '@/components/navbar'
import { octopus, OctopusEventTypeEnum } from '@/utils/octopus'

import '@/styles/page-base.less'
import '@/utils/statistics'

import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { matchRoutes } from 'react-router-dom'
import { index as routeConfig } from '@/routes'
import { useInitFrog } from '@/hooks/frog'
import { Layout, Spin, WhiteScreenTemplate } from '@yuanfudao/metis-web-component'
import { useRecoilValue } from 'recoil'
import { currentAccountAtom, useCurrentAccount, useFormModules } from './store/home'
import lessModule from './App.module.less'
import ErrorBoundary from './components/error-boundary'

const { Header, Content } = Layout

const noLoginPage = ['#/fast-login']

export default function App(props: any) {
  const navigate = useNavigate()
  const { hash } = window.location
  const cutHash = hash.split('?')[0]
  useGrades()
  useRegion()
  useAdminRegion()
  useSemesters()
  useCurrentAccount(cutHash)
  useInitFrog()
  const currentAccount = useRecoilValue(currentAccountAtom)
  const { getFormModules } = useFormModules()

  const location = useLocation()
  const { formid } = useParams()
  const [isLoginSuccess, setIsLoginSuccess] = useState<boolean>(false)

  useEffect(() => {
    const matches = matchRoutes(routeConfig, location.pathname)
    const viewName = (matches?.[matches.length - 1]?.route?.path as string) || location.pathname
    octopus.rum.startView({ name: viewName })
  }, [location.pathname])

  useEffect(() => {
    if (currentAccount?.username) {
      octopus.setUser({
        id: String(currentAccount.username),
        name: currentAccount.name,
        tenantId: getTokenTenantId(),
        isInternalUser: isInternalUser(),
        isDesignationUser: getUserLoginType()
          ? [USER_LOGIN_TYPE.DESIGNATION, USER_LOGIN_TYPE.TEMP_LINK].includes(getUserLoginType() as USER_LOGIN_TYPE)
          : false,
      })
      octopus.setGlobalContext({
        tenantName: currentAccount.tenantName,
      })
    }
  }, [currentAccount])

  useEffect(() => {
    if (cutHash && noLoginPage.includes(cutHash)) {
      // 免登录页面不校验登录
      return
    }
    // 未登录会自动跳登录页，不用写未登录逻辑
    checkoutLogin().then((isLogin: boolean) => {
      setIsLoginSuccess(isLogin)
      if (isLogin) {
        if (formid) {
          getFormModules(formid)
        }
      }
    })
  }, [formid, navigate, getFormModules, cutHash])

  const handleComponentDidCatch = useCallback((error: Error, info: { componentStack: string }) => {
    octopus.rum.addError(new Error('页面白屏'), {
      process: 'throwable',
      type: OctopusEventTypeEnum.WhiteScreenError,
      errorType: error.name || 'Error',
      originErrorMessage: JSON.stringify(error, Object.getOwnPropertyNames(error)) || '',
      errorMessage: error.message || '',
      componentStack: info.componentStack || '',
      url: window.location.href,
    })
  }, [])

  const onReportWhiteScreenHandle = useCallback((tinyCode: string, messageHandle: any, reportedDescription?:string) => {
    try {
      octopus.rum.addError(new Error(reportedDescription), {
        process: 'throwable',
        type: OctopusEventTypeEnum.WhiteScreenReport,
        tinyCode,
        reportedDescription,
        url: window.location.href,
      })
      messageHandle?.success?.()
    } catch (err) {
      messageHandle?.error?.()
    }
  }, [])

  return (
    <ErrorBoundary
      fallback={<WhiteScreenTemplate appName="Flying 信息收集平台" isReportErrorDescription changeModeGuideUrl="" chromiumMinimumSupportedVersion="86.0" onReportWhiteScreenHandle={onReportWhiteScreenHandle} />}
      componentDidCatch={handleComponentDidCatch}
    >
      {
        isLoginSuccess ? (
          <Layout className={`${lessModule.layout}`}>
            <Header className={lessModule['layout-header']}>
              <Navbar />
            </Header>
            <Content className={`${lessModule['layout-content']} fullScreenClassNameContainer`}>
              <Suspense fallback={<div className={lessModule.spinCenter}><Spin size="large" /></div>}>
                <Outlet />
              </Suspense>
            </Content>
          </Layout>
        ) : (
          // 免登录页
          <Suspense fallback={<div className={lessModule.spinCenter}><Spin size="large" /></div>}>
            <Outlet />
          </Suspense>
        )
      }
    </ErrorBoundary>
  )
}
